import { NullableClassName } from '@squareup/dex-types-shared-ui';
import { HttpMethods, ReferenceCard, Link } from '@squareup/dex-ui';
import { Markdown } from '@squareup/dex-ui-markdown';
import { Box, Heading30, Paragraph20 } from '@squareup/dex-ui-shared-base';
import React, { ReactNode } from 'react';

import styles from './reference-item.module.css';

interface ReferenceItemProps {
  title?: string | ReactNode | undefined;
  summary?: string | undefined;
  description?: string | undefined;
  isBeta?: boolean | undefined;
  isV1?: boolean | undefined;
  httpMethod?: string | undefined;
  path?: string | undefined;
  isDeprecated?: boolean | undefined;
  // Additional data that can be made inline with the heading of the item
  inlineHeadingChild?: ReactNode | undefined;
}

interface ReferenceSubItemProps {
  key: string;
  item: ReferenceItemProps;
  href?: React.ComponentProps<typeof Link>['href'];
  as?: string | undefined;
  className?: string | undefined;
}

type ReferenceItemWithSubItemsProps = {
  subItems?: ReferenceSubItemProps[];
} & ReferenceItemProps &
  NullableClassName;

const ReferenceItem = ({
  title,
  summary,
  description,
  subItems,
  inlineHeadingChild,
}: ReferenceItemWithSubItemsProps) => {
  const heading = (
    <Heading30 margin={{ top: inlineHeadingChild ? undefined : '5x' }}>
      {title}
    </Heading30>
  );
  return (
    <>
      {title && (
        <Box>
          {inlineHeadingChild ? (
            <Box className={styles.inline}>
              {heading}
              <Box className={styles.desktop}>{inlineHeadingChild}</Box>
            </Box>
          ) : (
            heading
          )}
          <Paragraph20
            margin={{ bottom: '2x', top: '1x' }}
            weight={description?.trim() ? 'medium' : undefined}
          >
            {summary}
          </Paragraph20>
          <Box margin={{ bottom: '3x' }}>
            {description?.trim() && <Markdown markdown={description} />}
          </Box>
        </Box>
      )}

      {subItems?.map(({ item, key, href, className, as }) => {
        const {
          title,
          summary,
          path,
          isBeta,
          isV1,
          httpMethod,
          description,
          isDeprecated,
        } = item;

        return (
          <Box
            className={className}
            as={as}
            key={key}
            margin={{ bottom: '2x', top: '2x' }}
          >
            <ReferenceCard
              description={summary || description}
              title={title || ''}
              href={href}
              path={path}
              isBeta={isBeta}
              isV1={isV1}
              isDeprecated={isDeprecated}
              httpMethod={httpMethod?.toUpperCase() as HttpMethods}
            />
          </Box>
        );
      })}
    </>
  );
};
export { ReferenceItem, type ReferenceItemProps, type ReferenceSubItemProps };
